import { useEffect, useMemo } from 'react';
import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { Button } from 'src/components';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { AuthCard } from 'src/auth/components/AuthCard';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { useInviteData } from 'src/auth/hooks/useInviteData';
import { InviteResendCard } from 'src/auth/invite/InviteResendCard';
import { setAcceptLinkRef } from 'src/auth/utils/inviteStorage';
import { isSsoOnly } from 'src/auth/utils/ory-helpers';
import { useFlags, useFlagsReady } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import { useSearchParams } from 'react-router';

interface DecodedUrlData {
  tenantId: string;
  token: string;
}

interface EncodedData {
  t: string;
  k: string;
}

const decodeUrlData = (encodedData: string | null): DecodedUrlData | null => {
  if (!encodedData) return null;

  try {
    const decoded = atob(encodedData.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded) as EncodedData;
    return {
      tenantId: parsed.t,
      token: parsed.k,
    };
  } catch {
    return null;
  }
};

const errorMessage = (isInviteError: boolean) =>
  isInviteError
    ? 'This invitation link has expired or is invalid. Please contact your administrator for a new invitation.'
    : 'Something went wrong. Please try refreshing the page.';

export const AcceptLinkPage = () => {
  const [searchParams] = useSearchParams();
  const encodedData = searchParams.get('data');

  const urlData = useMemo(() => decodeUrlData(encodedData), [encodedData]);

  const {
    data: inviteData,
    isLoading: isInviteLoading,
    isError: isInviteError,
    error: inviteError,
  } = useInviteData(urlData?.tenantId, urlData?.token);

  const {
    config,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = usePublicTenantConfig(urlData?.tenantId);

  const { flagEnabledExpiredInviteSelfResend } = useFlags();
  const flagsReady = useFlagsReady();

  useEffect(() => {
    if (isInviteError) {
      Logger.warn('Failed to fetch invite data', {
        tenantId: urlData?.tenantId,
        error: inviteError,
        tokenEnding: urlData?.token ? `...${urlData.token.slice(-4)}` : 'no token',
      });
    }
    if (isConfigError) {
      Logger.warn('Failed to fetch tenant config', {
        tenantId: urlData?.tenantId,
        error: configError,
        tokenEnding: urlData?.token ? `...${urlData.token.slice(-4)}` : 'no token',
      });
    }
  }, [isInviteError, isConfigError, urlData?.tenantId, inviteError, configError]);

  const handleContinue = () => {
    if (inviteData?.ory_link && urlData) {
      // Ory's link is single use and the pages it can dead-end on carry no invite details,
      // so the accept-link goes with the invitee for the rest of the round trip.
      setAcceptLinkRef(urlData);
      window.location.href = inviteData.ory_link;
    }
  };

  if (!urlData) {
    return (
      <AuthPageLayout withHeader>
        <AuthCard>
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Invalid or missing invite data. Please check your invitation link or contact your
            administrator.
          </Alert>
        </AuthCard>
      </AuthPageLayout>
    );
  }

  const isLoading = isInviteLoading || isConfigLoading;
  const hasError = isInviteError || isConfigError;

  const variant = !isConfigError && config?.authMethod ? config.authMethod : 'password';
  const isSso = isSsoOnly(variant);

  if (hasError) {
    if (!flagsReady) {
      return <AuthPageLayout isLoading withHeader />;
    }

    const canSelfResend = isInviteError && flagEnabledExpiredInviteSelfResend;

    return (
      <AuthPageLayout isLoading={isLoading} withHeader>
        <AuthCard>
          {canSelfResend ? (
            <InviteResendCard tenantId={urlData.tenantId} token={urlData.token} />
          ) : (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {errorMessage(isInviteError)}
            </Alert>
          )}
        </AuthCard>
      </AuthPageLayout>
    );
  }

  if (!inviteData) {
    return <AuthPageLayout isLoading={isLoading} withHeader />;
  }

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      <Stack align="center" gap="md">
        <Text c="gray.0" fz={36} fw={FONT_WEIGHTS.bold}>
          {inviteData.first_name ? `Welcome, ${inviteData.first_name}!` : 'Welcome!'}
        </Text>
        <Text c="gray.0" ta="center" fz={24} fw={FONT_WEIGHTS.medium}>
          {inviteData.inviter_name
            ? `${inviteData.inviter_name} has invited you to join Afternoon.`
            : 'You have been invited to join Afternoon.'}
          <br />
          {isSso
            ? 'Please sign in with your organization account to continue.'
            : 'Please create your profile to continue.'}
        </Text>
        {inviteData.email && (
          <Text c="dimmed" size="sm">
            {inviteData.email}
          </Text>
        )}

        <Button onClick={handleContinue} size="lg">
          {isSso ? 'Continue with SSO' : 'Accept invite & create profile'}
        </Button>
      </Stack>
    </AuthPageLayout>
  );
};
